import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserIdleService implements OnDestroy {
  // Mirror angular-user-idle config currently in use:
  //   UserIdleModule.forRoot({ idle: 900, timeout: 10, ping: 120 })
  private readonly idleMs = 900 * 1000;
  private readonly timeoutMs = 10 * 1000;

  private readonly timerStart$ = new Subject<void>();
  private readonly timeout$ = new Subject<void>();

  private activitySub?: Subscription;
  private idleHandle?: number;
  private timeoutHandle?: number;

  constructor(private readonly zone: NgZone) {}

  startWatching(): void {
    this.stopWatching();
    this.zone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll', { passive: true } as AddEventListenerOptions),
        fromEvent(document, 'touchstart', { passive: true } as AddEventListenerOptions),
      );
      this.activitySub = activity$.subscribe(() => this.resetIdleCountdown());
      this.resetIdleCountdown();
    });
  }

  stopWatching(): void {
    this.activitySub?.unsubscribe();
    this.activitySub = undefined;
    this.clearTimers();
  }

  onTimerStart(): Observable<void> {
    return this.timerStart$.asObservable();
  }

  onTimeout(): Observable<void> {
    return this.timeout$.asObservable();
  }

  ngOnDestroy(): void {
    this.stopWatching();
    this.timerStart$.complete();
    this.timeout$.complete();
  }

  private resetIdleCountdown(): void {
    this.clearTimers();
    this.idleHandle = setTimeout(() => {
      this.zone.run(() => this.timerStart$.next());
      this.timeoutHandle = setTimeout(() => {
        this.zone.run(() => this.timeout$.next());
      }, this.timeoutMs);
    }, this.idleMs);
  }

  private clearTimers(): void {
    if (this.idleHandle !== undefined) {
      clearTimeout(this.idleHandle);
      this.idleHandle = undefined;
    }
    if (this.timeoutHandle !== undefined) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
    }
  }
}
